<template>
  <div :class="{
    'non-default-calendar-layout': layout !== 'default',
    'calendar-layout-extended': layout === 'extended' || layout === 'double-row-extended',
    'hide-default-title': layout === 'extended' && customCalendarTitle,
    'disable-calendar-scroll': disableCalendarScroll
  }">
    <div v-if="calendarKeys.length" class="key-container">
      <div  id="calKey">
        <div
            v-for="(k, i) in calendarKeys"
            :key="i"
            class="key-item align-items-center me-2">
            <span
                class="status p-2 me-2"
                :style="`background-color: ${k.colour}`"
            />
          <small>{{ k.title }}</small>
        </div>
      </div>
    </div>

    <p v-if="layout === 'extended' && customCalendarTitle" class="custom-title mt-3 mb-0">{{ customCalendarTitle }}</p>

    <Calendar
        class="session-calendar"
        expanded
        transparent
        borderless
        :initial-page="{
          day: minDate.getDay(),
          month: minDate.getMonth(),
          year: minDate.getFullYear(),
        }"
        title-position="center"
        :attributes="attributes"
        :from-date="minDate"
        @dayclick="onDayClick"
        v-bind="calendarProps"
    />
  </div>

</template>

<script setup>
import {Calendar} from "v-calendar";
import {computed, ref} from "vue";
import {dateRange, isSameDate} from "../functions/dateFunctions";

const props = defineProps({
  minDate: {
    type: Date,
    required: true
  },
  maxDate: {
    type: Date,
    required: true
  },
  useCalendarDates: {
    type: [Boolean, Number],
    default: false,
    required: false,
  },
  calendarDates: {
    type: Array,
    required: true
  },
  calendarBgColour: {
    type: String,
    required: true
  },
  calendarTextColour: {
    type: String,
    required: true
  },
  sessionDates: {
    type: Array,
    required: true,
  },
  layout: {
    type: String,
    default: 'default'
  },
  customCalendarTitle: {
    type: String,
    required: false,
  },
  disableCalendarScroll: {
    type: Boolean,
    default: false,
  },
  customDates: {
    type: Object,
    required: false,
    default: null
  },
  showSoldOutDatesObject: {
    type: Object,
    required: false,
    default: () => ({ enabled: false, dates: [] }),
  },
});

const minDate = computed(() => props.minDate);
const maxDate = computed(() => props.maxDate);

// Conditionally generate props for <Calendar>
const calendarProps = computed(() => {
  const propsOut = {}

  if (props.layout === 'double-row' || props.layout === 'double-row-extended') {
    propsOut.rows = 2
  }

  if (props.layout === 'weekly') {
    propsOut.view = 'weekly'
  }

  if (props.disableCalendarScroll) {
    // Disables touchscreen swiping
    propsOut.disablePageSwipe = true
  }

  // CUSTOM EVENT DATES (Single Day, Date Range, Recurring Dates, Recurring Range)
  const dateWindows = Array.isArray(props.customDates?.dateWindows) ? props.customDates.dateWindows : [];
  const isValidWin = (w) => w && w.start && w.end;
  const effectiveDateWindows = (Array.isArray(dateWindows) ? dateWindows : []).filter(isValidWin);

  if (effectiveDateWindows.length) {
    const starts = effectiveDateWindows.map(w => new Date(w.start + 'T00:00:00').getTime());
    const ends   = effectiveDateWindows.map(w => new Date(w.end   + 'T23:59:59').getTime());
    propsOut.minDate = new Date(Math.min(...starts));
    propsOut.maxDate = new Date(Math.max(...ends));

    // Constrain selectable days to just the CMS windows
    propsOut.availableDates = effectiveDateWindows.map(w => ({
      start: new Date(w.start + 'T00:00:00'),
      end:   new Date(w.end   + 'T23:59:59'),
    }));
  } else {
    // No CMS date windows → DT-only behaviour (or no constraints)
    propsOut.minDate = minDate.value;
    propsOut.maxDate = maxDate.value;
  }
  // CUSTOM EVENT DATES END

  return propsOut
})

// Helpers to filter session dates against windows (if provided)
const normalizedDateWindows = computed(() => {
  const rawWins = Array.isArray(props.customDates?.dateWindows) ? props.customDates.dateWindows : [];
  const wins = rawWins.filter(w => w && w.start && w.end);
  return wins.map(win => ({
    start: new Date(win.start + 'T00:00:00'),
    end:   new Date(win.end   + 'T23:59:59'),
  }));
});

const isInAnyDateWindow = (date) => {
  if (!normalizedDateWindows.value.length) return true;
  const t = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  return normalizedDateWindows.value.some(w => t >= w.start.getTime() && t <= w.end.getTime());
};
// CUSTOM EVENT DATES END

// SHOW SOLD OUT DATES
// Computed list of sold-out dates from prop
const soldOutDates = computed(() => {
  return props.showSoldOutDatesObject.enabled
      ? props.showSoldOutDatesObject.dates
      : [];
});


const emit = defineEmits(['dayClick']);

// Guard clicks: only emit when the date is inside any CMS date window AND has at least one session
const onDayClick = (evt) => {
  const d = evt?.date instanceof Date ? evt.date : new Date(evt?.date);
  // 1) Must be within allowed date windows
  if (!isInAnyDateWindow(d)) return;
  // 2) Must correspond to a date that actually has sessions
  const hasSession = sortedSessionDates.value.some((s) => isSameDate(s, d));
  if (!hasSession) return;
  emit('dayClick', evt);
};

////////////////////////////
// Computed
////////////////////////////
const sortedSessionDates = computed(() => {
  const filtered = (props.sessionDates || []).filter(d => {
    const dt = (d instanceof Date) ? d : new Date(d);
    return isInAnyDateWindow(dt);
  });
  return filtered.sort((a, b) => {
    const ta = (a instanceof Date) ? a.getTime() : new Date(a).getTime();
    const tb = (b instanceof Date) ? b.getTime() : new Date(b).getTime();
    return ta - tb;
  });
});

const calendarKeys = computed(() => {
  // If we aren't using calendar item dates, then there's no need to show a key as everything
  // shows up as the default calendar background colour.
  if (!props.useCalendarDates) {
    return [];
  }

  // This is a HUGE hack, but it works and is the simplest way of getting a unique list of calendar
  // items without checking each property individually.
  return Array.from(
      new Set(
          props.calendarDates
              .map((d) => ({...d, dates: dateRange(d.start_date, d.end_date)}))
              .filter((c) => c.dates.filter((d) => sortedSessionDates.value.some((s) => isSameDate(s, d) && isInAnyDateWindow(d))).length)
              .map((c) => ({
                colour: c.calendar_item.hex_colour,
                title: c.calendar_item.title,
              }))
              .map(JSON.stringify)
      )
  ).map(JSON.parse)
});


////////////////////////////
// State
////////////////////////////
const attributes = ref([
  // Base calendar colours as configured by the CMS
  {
    key: 'sold-out',
    bar: {
      class: 'sold-out-pill' // Unique class to target with CSS
    },
    dates: soldOutDates, // Uses the computed list
    order: 2, // Highest order ensures it layers on top of colors (0) and calendar items (1)
  },
  {
    highlight: {
      style: {backgroundColor: props.calendarBgColour},
      contentStyle: {
        color: props.calendarTextColour,
        padding: '20px',
      },
      fillMode: 'solid',
    },
    dates: sortedSessionDates,
    order: 0 // Order works like z-index, so we use 0 here as a fallback for when no custom config is present for a given date
  },
  // Calendar date colours as configured by the CMS
  ...props.useCalendarDates ? props.calendarDates.map((d) => ({
    highlight: {
      style: {backgroundColor: d.calendar_item.hex_colour},
      contentStyle: {
        color: d.calendar_item.text_colour,
        padding: '20px',
      },
      fillMode: 'solid',
    },
    dates: dateRange(d.start_date, d.end_date)
        .filter((d) => isInAnyDateWindow(d))
        .filter((d) => sortedSessionDates.value.some((s) => isSameDate(s, d))),
    order: 1
  })) : [],
]);

</script>

<style scoped lang="scss">
.status {
  height: 10px;
  width: 10px;
  display: inline-block;
  border-radius: 50%;
}

.key-title {
  i {
    transition: all ease-in-out 0.5s;
    position: relative;
    top: 4px;
  }

  &[aria-expanded="true"] {
    i {
      transform: rotate(180deg);
      transform-origin: center
    }
  }
}

</style>

<style lang="scss">
.key-container {
  width: 100%;
  overflow-x: auto;
  white-space: nowrap; /* Prevents line breaks */
}
#calKey {
  display: flex; /* Aligns items in a row */
  flex-wrap: nowrap; /* Prevents wrapping */
  gap: 10px; /* Adds space between items */
  padding: 10px;
  min-width: max-content; /* Ensures the container stretches if needed */
  .key-item {
    display: flex;
    align-items: center;
    gap: 5px;
    flex-shrink: 0; /* Prevents shrinking */
  }
}

.vc-light {
  background: #fff;
  background-color: #fff
}

.vc-day-content {
  width: 100% !important;
  height: 100% !important;
  border: 1px solid #fff;
  padding: 0 !important;
  border-radius: unset !important;
  &.vc-disabled {
    background: #fff; text-decoration: line-through; pointer-events: none; color: var(--vc-day-content-disabled-color) !important; font-weight: 500;
  }
  &:not(.vc-highlight-content-solid) {
    text-decoration: line-through;   color: var(--vc-day-content-disabled-color);
  }
}

.vc-day.in-month {
  background: #fff;
}

.session-calendar {
  .vc-header {
    padding: 0;

    .vc-title, .vc-arrow {
      background: none;
    }

    .vc-arrow {   width: 36px; height: 36px;
      border-radius: 50%; padding: 6px;
      border: 1px solid #d3d3d3;
    }

  }
}
.vc-weeks {
  padding: 0 !important;
}

.vc-weekday {
  padding: 20px 0 !important;
}

.vc-highlight {
}

.vc-day {
  aspect-ratio: 1;
}

.vc-highlight-bg-solid {
  border-radius: unset !important;
  width: 100% !important;
  height: 100% !important;
}

.vc-nav-item.is-active {
  background-color: #000 !important;
}

.vc-popover-content {
  background-color: #fff !important;
  //button { font-size: 30px; }
}

.collapse.show {
  display: flex;
}


// CLASSES FOR ALTERNATE CALENDAR LAYOUTS
.non-default-calendar-layout {
  // Re-adds margin that is missing from non-default views
  margin-bottom: 64px;
}

.calendar-layout-extended {
  .is-not-in-month * {
    // Unhides calendar days for Extended Month view
    opacity: 1 !important;
    pointer-events: inherit !important;
  }
}
.hide-default-title {
  // Hides default title and styles custom title
  .session-calendar {
    .vc-header {
      .vc-title { display: none; }
    }
  }

  .custom-title {
    font-size: 18px;
    font-weight: 600;
    line-height: 30px;
    font-family: "system-ui", -apple-system, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", Helvetica, Arial, sans-serif;
  }
}

// Hide element that contains nav arrows and title when both are hidden
.hide-default-title.disable-calendar-scroll {
  .session-calendar {
    .vc-header {
      display: none;
    }
  }
}

.disable-calendar-scroll {
  .session-calendar .vc-arrow {
    // Remove navigation buttons
    display: none;
  }
}

// SHOW SOLD OUT DATES ON CALENDAR
.vc-day {
  position: relative;
}

.vc-bars .sold-out-pill {
  // Hide the default bar color/shape but keep it for positioning
  position: relative;
  background-color: transparent !important;
  border-radius: 0;
  width: 0;
  height: 0;
}

.vc-bars .sold-out-pill::after {
  content: 'Sold Out';
  font-size: 10px;
  color: #FFF;
  border-radius: 40px;
  background-color: #be0031;
  padding: 2px 6px;
  font-weight: 600;
  line-height: 1;
  // Positioning within the day cell/bar area
  position: absolute;
  top: -60px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  display: block;
  white-space: nowrap;
}

.vc-day:has(.sold-out-pill) {
  .vc-day-content {
    color: #000 !important;
    text-decoration: none !important;
    pointer-events: none;
  }
}
// END SHOW SOLD OUT DATES ON CALENDAR

</style>